import { Fragment, type ReactNode } from 'react'

/**
 * Display-only rendering for the plain-text preview pane.
 *
 * Plain-text sequence bodies are stored and sent as raw text — anchors are
 * stripped for deliverability (see `EditorCore` `plainText`). But a reviewer
 * checking a step needs to click a link (e.g. a rebate URL) to confirm it
 * points where intended. `linkifyPlainTextPreview` wraps bare `http(s)` URLs
 * in anchors for the preview render ONLY. It never mutates the body: the string
 * saved to the DB and sent through Bison is untouched — callers pass the same
 * body they would have rendered as text.
 *
 * Non-URL segments get `applyPreviewDisplayFixes` (hyphen/dash line-break
 * fixes); a URL's `href` is the literal matched URL so those fixes never
 * corrupt the link target.
 */

/**
 * Non-breaking fixes for the plain-text preview.
 *
 * Unicode's line-breaking algorithm (UAX #14) treats hyphen (`-`), em dash
 * (`—`), and en dash (`–`) as soft break opportunities — the browser will wrap
 * a line after one even when the preview pane has CSS to prevent it. This is
 * the same behavior recipients see in a narrow email-client pane, but in our
 * editor it confuses operators who think the rendered output has a real break.
 *
 * Display-only: the bytes saved to the DB and sent through Bison are unchanged.
 * Exported for unit tests; `linkifyPlainTextPreview` is the only runtime caller.
 */
export function applyPreviewDisplayFixes(text: string): string {
  return (
    text
      // Hyphen between letters → U+2011 (non-breaking hyphen). Lookaround
      // (vs. a capturing pattern) handles chained hyphens like `state-of-the-art`
      // — a capture-and-replace match consumes the trailing letter and skips
      // the next hyphen, leaving it still breakable.
      .replace(/(?<=[A-Za-z])-(?=[A-Za-z])/g, '‑')
      // Em dash and en dash → wrap with U+2060 (Word Joiner) on each side so
      // the browser won't break a line after them. Word Joiner is zero-width.
      .replace(/([—–])/g, '⁠$1⁠')
      // Lone hyphen between spaces (e.g. `Hi - thanks`) → also wrap with Word
      // Joiners. UAX #14 treats this as a break opportunity; in our narrow
      // preview pane that produces an unintended line break after the dash.
      .replace(/(?<=\s)-(?=\s)/g, '⁠-⁠')
  )
}

// Capturing group so `String.split` keeps the URLs: the result alternates
// plain-text (even indices) and matched-URL (odd indices) segments.
const URL_PATTERN = /(https?:\/\/[^\s]+)/

/**
 * Trailing characters that are almost always sentence punctuation rather than
 * part of a URL. A closing paren is handled separately so balanced links like
 * `…/Foo_(bar)` keep their paren while a flattened `(https://url)` does not.
 * Em/en dashes are included because editors auto-convert `--` into `—`, so a
 * dash butted against the end of a URL (`https://site.com— more`) is a
 * separator, not part of the link. A dash mid-URL (`…/a—b`) is left intact.
 */
const TRAILING_PUNCTUATION = /[.,;:!?'"—–]+$/

function trimTrailingPunctuation(url: string): {
  href: string
  trailing: string
} {
  let href = url
  let trailing = ''

  // Loop so mixed trailers like `url).` peel off fully.
  let changed = true
  while (changed) {
    changed = false

    const punct = href.match(TRAILING_PUNCTUATION)
    if (punct) {
      trailing = punct[0] + trailing
      href = href.slice(0, -punct[0].length)
      changed = true
    }

    // A trailing ')' belongs to the URL only if it closes a '(' opened inside
    // it. Compare depth (not mere presence) so a URL with its own balanced
    // paren wrapped in an outer sentence paren — `…/Foo_(bar))` — still sheds
    // the stray outer ')'.
    if (href.endsWith(')')) {
      const opens = (href.match(/\(/g) ?? []).length
      const closes = (href.match(/\)/g) ?? []).length
      if (closes > opens) {
        trailing = ')' + trailing
        href = href.slice(0, -1)
        changed = true
      }
    }
  }

  return { href, trailing }
}

export function linkifyPlainTextPreview(text: string): ReactNode {
  return text.split(URL_PATTERN).map((segment, i) => {
    // Even segments are plain text; odd segments are the captured URLs.
    if (i % 2 === 0) {
      return segment ? (
        <Fragment key={i}>{applyPreviewDisplayFixes(segment)}</Fragment>
      ) : null
    }

    const { href, trailing } = trimTrailingPunctuation(segment)
    return (
      <Fragment key={i}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand underline"
        >
          {href}
        </a>
        {trailing}
      </Fragment>
    )
  })
}
